import React from "react";
import PropTypes from "prop-types";
import { AiProcessingDisplayStatusEnum } from "Constants/Enums/AiProcessingDisplayStatusEnum";

// ============================================================
// Shared design system for the Applicant Detail page.
// Inspired by Mudassir's Talent Profile HTML template.
// ============================================================

export const theme = {
  fontFamily:
    "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, sans-serif",
  bg: "#f0f4f0",
  surface: "#ffffff",
  border: "#e6ede6",
  borderSoft: "#eef4ee",
  // Brand green, matches the rest of the VelorHR app (sidebar, logo, highlights).
  accent: "#2b7e3a",
  accentDeep: "#0f5a2a",
  // Note: kept the `navyGradient` key for backwards compat; it now renders the
  // brand green gradient. Don't be confused by the name.
  navyGradient: "linear-gradient(135deg, #0f5a2a 0%, #1e8c45 100%)",
  text: "#1e2f1e",
  textMuted: "#5b7060",
  textLabel: "#617e63",
  pillSkillBg: "#eef3fc",
  pillSkillText: "#1a4c7a",
  pillDomainBg: "#e7edf4",
  pillDomainText: "#1e2f3e",
  green: "#2b7e3a",
  greenBg: "#dff5e3",
  orange: "#c26b2c",
  orangeBg: "#fff0db",
  red: "#b13e3e",
  redBg: "#fff0ee",
  blue: "#1677ff",
  blueBg: "#eef3ff",
  shadowCard: "0 6px 14px rgba(0,0,0,0.02), 0 1px 3px rgba(0,0,0,0.03)",
  shadowKpi: "0 5px 14px rgba(0,0,0,0.02), 0 2px 6px rgba(0,0,0,0.05)",
  shadowHero: "0 12px 22px rgba(15,90,42,0.12)",
};

// =================== Page wrapper =====================
export function PageBg({ children, style }) {
  return (
    <div
      className="ad-page-bg"
      style={{
        background: theme.bg,
        fontFamily: theme.fontFamily,
        color: theme.text,
        padding: "1.5rem",
        minHeight: "100%",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
PageBg.propTypes = { children: PropTypes.node, style: PropTypes.object };

// =================== Hero (navy gradient) =====================
export function Hero({ title, subtitle, badges = [], actions, rightSlot }) {
  return (
    <div
      style={{
        background: theme.navyGradient,
        borderRadius: "32px",
        padding: "1.5rem 2rem",
        marginBottom: "1.5rem",
        color: "white",
        boxShadow: theme.shadowHero,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "1rem",
      }}
    >
      <div style={{ flex: "1 1 60%", minWidth: "260px" }}>
        <h1
          style={{
            fontSize: "1.8rem",
            fontWeight: 600,
            letterSpacing: "-0.3px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            margin: 0,
            flexWrap: "wrap",
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              marginTop: "10px",
              opacity: 0.9,
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </p>
        )}
        {badges.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginTop: "12px",
            }}
          >
            {badges.map((b, idx) => (
              <span
                key={idx}
                style={{
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: "40px",
                  padding: "4px 14px",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {b}
              </span>
            ))}
          </div>
        )}
        {actions && <div style={{ marginTop: "14px" }}>{actions}</div>}
      </div>
      {rightSlot && <div>{rightSlot}</div>}
    </div>
  );
}
Hero.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  badges: PropTypes.array,
  actions: PropTypes.node,
  rightSlot: PropTypes.node,
};

// =================== Card =====================
export function Card({ title, icon, children, style, accentColor }) {
  return (
    <div
      style={{
        background: theme.surface,
        borderRadius: "28px",
        padding: "1.5rem",
        boxShadow: theme.shadowCard,
        border: `1px solid ${theme.border}`,
        marginBottom: "1.5rem",
        ...style,
      }}
    >
      {title && (
        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: 600,
            marginBottom: "1.2rem",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            borderLeft: `4px solid ${accentColor || theme.accent}`,
            paddingLeft: "14px",
            color: theme.accentDeep,
            marginTop: 0,
          }}
        >
          {icon && <span>{icon}</span>}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
Card.propTypes = {
  title: PropTypes.node,
  icon: PropTypes.node,
  children: PropTypes.node,
  style: PropTypes.object,
  accentColor: PropTypes.string,
};

// =================== KPI Card =====================
export function KpiCard({ label, value, unit, subtitle, smallValue }) {
  return (
    <div
      style={{
        background: theme.surface,
        borderRadius: "24px",
        padding: "1.3rem 1.2rem",
        boxShadow: theme.shadowKpi,
        border: `1px solid ${theme.border}`,
      }}
    >
      <div
        style={{
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontWeight: 600,
          color: theme.textMuted,
          marginBottom: "0.5rem",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: smallValue ? "1.4rem" : "2.2rem",
          fontWeight: 700,
          color: "#0f2b3d",
          lineHeight: 1.2,
          textTransform: smallValue ? "capitalize" : "none",
        }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: "0.9rem",
              fontWeight: 400,
              color: "#6a7f9b",
              marginLeft: "4px",
            }}
          >
            {unit}
          </span>
        )}
      </div>
      {subtitle && (
        <div
          style={{
            display: "inline-block",
            background: "#f0f2f5",
            borderRadius: "20px",
            padding: "0.2rem 0.8rem",
            fontSize: "0.75rem",
            fontWeight: 500,
            marginTop: "0.5rem",
            color: theme.textMuted,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}
KpiCard.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  unit: PropTypes.string,
  subtitle: PropTypes.node,
  smallValue: PropTypes.bool,
};

// =================== Pill =====================
const pillVariants = {
  skill: { bg: theme.pillSkillBg, color: theme.pillSkillText },
  domain: { bg: theme.pillDomainBg, color: theme.pillDomainText },
  success: { bg: theme.greenBg, color: theme.green },
  warning: { bg: theme.orangeBg, color: theme.orange },
  danger: { bg: theme.redBg, color: theme.red },
  info: { bg: theme.blueBg, color: "#1f4b8a" },
  neutral: { bg: "#e2e8f0", color: "#25455f" },
  dark: { bg: "rgba(31,59,76,0.12)", color: "#1f3b4c" },
};
export function Pill({ variant = "skill", children, style }) {
  const v = pillVariants[variant] || pillVariants.skill;
  return (
    <span
      style={{
        background: v.bg,
        color: v.color,
        padding: "5px 14px",
        borderRadius: "40px",
        fontSize: "0.8rem",
        fontWeight: 500,
        display: "inline-block",
        ...style,
      }}
    >
      {children}
    </span>
  );
}
Pill.propTypes = {
  variant: PropTypes.string,
  children: PropTypes.node,
  style: PropTypes.object,
};

// =================== Pill row =====================
function getPillLabel(item) {
  if (item == null) return "";
  if (typeof item === "string") return item;
  if (typeof item === "number") return String(item);
  return (
    item.name ||
    item.skillName ||
    item.skill ||
    item.title ||
    item.label ||
    item.text ||
    item.description ||
    ""
  );
}

export function PillRow({ items, variant = "skill", emptyText = "—", renderItem }) {
  if (!items || items.length === 0) {
    return (
      <div style={{ color: theme.textMuted, fontSize: "0.85rem" }}>{emptyText}</div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "8px",
      }}
    >
      {items.map((item, idx) => {
        const label = renderItem ? renderItem(item) : getPillLabel(item);
        return (
          <Pill key={idx} variant={variant}>
            {label || "—"}
          </Pill>
        );
      })}
    </div>
  );
}
PillRow.propTypes = {
  items: PropTypes.array,
  variant: PropTypes.string,
  emptyText: PropTypes.string,
  renderItem: PropTypes.func,
};

// =================== Label meta =====================
export function LabelMeta({ children, style }) {
  return (
    <div
      style={{
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: theme.textLabel,
        fontWeight: 600,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
LabelMeta.propTypes = { children: PropTypes.node, style: PropTypes.object };

// =================== Divider =====================
export function Divider({ style }) {
  return (
    <hr
      style={{
        margin: "1rem 0",
        border: "none",
        borderTop: `1px solid ${theme.borderSoft}`,
        ...style,
      }}
    />
  );
}
Divider.propTypes = { style: PropTypes.object };

// =================== States =====================
export function LoadingState({ label = "Loading..." }) {
  return (
    <div
      style={{
        padding: "40px",
        textAlign: "center",
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
        fontSize: "0.95rem",
      }}
    >
      {label}
    </div>
  );
}
LoadingState.propTypes = { label: PropTypes.string };

export function EmptyState({ title, hint, icon = "📭" }) {
  return (
    <div
      style={{
        padding: "48px 24px",
        textAlign: "center",
        color: theme.textMuted,
        fontFamily: theme.fontFamily,
      }}
    >
      <div style={{ fontSize: "2.4rem", marginBottom: "8px" }}>{icon}</div>
      <div style={{ fontSize: "1rem", fontWeight: 500, color: theme.text }}>
        {title}
      </div>
      {hint && (
        <div
          style={{
            fontSize: "0.85rem",
            marginTop: "6px",
            fontStyle: "italic",
            color: theme.textLabel,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
EmptyState.propTypes = {
  title: PropTypes.string,
  hint: PropTypes.string,
  icon: PropTypes.string,
};

// =================== AI unavailable banner =====================
// Per Mudassir (May-21): when the external AI service is down, the backend
// returns 200 with null AI fields instead of a 500. Render this banner at
// the top of any AI-derived section to make the gap obvious to HR.
export function AIUnavailableBanner({ message, style }) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
        border: "1px solid #fcd34d",
        borderLeft: "4px solid #f59e0b",
        borderRadius: "14px",
        padding: "12px 16px",
        margin: "0 0 1rem 0",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        fontFamily: theme.fontFamily,
        ...style,
      }}
    >
      <span style={{ fontSize: "1.4rem" }}>🤖</span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "0.88rem",
            fontWeight: 600,
            color: "#92400e",
            marginBottom: "2px",
          }}
        >
          AI temporarily unavailable
        </div>
        <div style={{ fontSize: "0.78rem", color: "#7c5e10", lineHeight: 1.4 }}>
          {message ||
            "AI-generated insights are not available right now. Please check back shortly."}
        </div>
      </div>
    </div>
  );
}
AIUnavailableBanner.propTypes = {
  message: PropTypes.string,
  style: PropTypes.object,
};

// Soft alert shown above an AI evaluation/profile when the displayed data may
// not reflect the latest application changes. Driven by the backend's
// `evaluationStatus.status` (per Mudassir, June-02). Renders nothing when the
// data is Current or the status is missing, so it's safe to mount
// unconditionally — it only appears when there's something to warn about.
export function StaleEvaluationBanner({ status, lastSuccessfulAt, label = "AI evaluation", style }) {
  const variants = {
    [AiProcessingDisplayStatusEnum.Stale]: {
      accent: "#f59e0b",
      bg: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
      border: "#fcd34d",
      titleColor: "#92400e",
      bodyColor: "#7c5e10",
      icon: "⚠️",
      title: `${label} may be out of date`,
      body: `The values shown may not reflect the latest application changes — a newer refresh failed or is still pending.`,
    },
    [AiProcessingDisplayStatusEnum.Processing]: {
      accent: "#1677ff",
      bg: "linear-gradient(135deg, #eff6ff 0%, #f5f9ff 100%)",
      border: "#bcdcff",
      titleColor: "#1e40af",
      bodyColor: "#3a5a99",
      icon: "🔄",
      title: `${label} is updating`,
      body: `A refresh is currently in progress. The values below may change once it completes.`,
    },
    [AiProcessingDisplayStatusEnum.Failed]: {
      accent: "#ef4444",
      bg: "linear-gradient(135deg, #fff1f0 0%, #fff7f6 100%)",
      border: "#fecaca",
      titleColor: "#991b1b",
      bodyColor: "#9a4a4a",
      icon: "⚠️",
      title: `${label} could not be completed`,
      body: `The latest AI processing attempt failed. Showing the last available data, if any.`,
    },
  };

  const v = variants[status];
  if (!v) return null; // Current (0), null, or unknown → no banner

  return (
    <div
      style={{
        background: v.bg,
        border: `1px solid ${v.border}`,
        borderLeft: `4px solid ${v.accent}`,
        borderRadius: "14px",
        padding: "12px 16px",
        margin: "0 0 1rem 0",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        fontFamily: theme.fontFamily,
        ...style,
      }}
    >
      <span style={{ fontSize: "1.4rem" }}>{v.icon}</span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "0.88rem",
            fontWeight: 600,
            color: v.titleColor,
            marginBottom: "2px",
          }}
        >
          {v.title}
        </div>
        <div style={{ fontSize: "0.78rem", color: v.bodyColor, lineHeight: 1.4 }}>
          {v.body}
          {lastSuccessfulAt && (
            <span style={{ opacity: 0.85 }}>
              {" "}
              Last successful update: {formatDateTime(lastSuccessfulAt)}.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
StaleEvaluationBanner.propTypes = {
  status: PropTypes.number,
  lastSuccessfulAt: PropTypes.string,
  label: PropTypes.string,
  style: PropTypes.object,
};

// =================== Experience-style card =====================
export function HighlightCard({ children, accent, style }) {
  return (
    <div
      style={{
        background: "#fafdff",
        borderLeft: `3px solid ${accent || "#7c9bc0"}`,
        padding: "1rem 1.1rem",
        borderRadius: "20px",
        marginTop: "0.8rem",
        ...style,
      }}
    >
      {children}
    </div>
  );
}
HighlightCard.propTypes = {
  children: PropTypes.node,
  accent: PropTypes.string,
  style: PropTypes.object,
};

// =================== Helpers =====================
// Defensive guard against backend sending DateTime.MinValue (year 0001) when
// a field isn't populated. Without this, raw .toLocaleString() renders
// "Jan 1, 1, 12:00 AM" which looks broken (per Sara May-23).
function isDefaultDate(date) {
  return !date || isNaN(date.getTime()) || date.getFullYear() < 1900;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isDefaultDate(d)) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isDefaultDate(d)) return "—";
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export function capitalize(str) {
  if (!str) return "—";
  return String(str).charAt(0).toUpperCase() + String(str).slice(1);
}
